<template>
  <header
    class="page-header py-3"
    :class="compact ? 'page-header-compact py-md-4 mb-0' : 'py-md-4 mb-3'"
  >
    <!-- A custom text (home dashboard notice) replaces title and introduction; actions stay right. -->
    <div v-if="$slots.text" class="d-flex align-items-center gap-3 page-header-notice">
      <div class="flex-grow-1"><slot name="text" /></div>
      <div class="page-header-actions"><slot /></div>
    </div>
    <template v-else>
      <h1 class="page-header-title">{{ title }}</h1>
      <!-- With a breadcrumb the introduction sits above it; without one it shares the toolbar's row. -->
      <p
        v-if="introduction && !compact && crumbs.length > 0 && !$slots.intro"
        class="page-header-introduction lead mb-0"
      >
        {{ introduction }}
      </p>
      <!-- This row is always there, so the header keeps its size without an introduction. -->
      <div
        class="page-header-row d-flex gap-3"
        :class="compact ? 'align-items-center' : 'align-items-end mt-3'"
      >
        <!-- The intro slot (dashboard notice) takes the place of the introduction and breadcrumb. -->
        <slot name="intro">
          <p
            v-if="introduction && (compact || crumbs.length === 0)"
            class="mb-0"
            :class="{'page-header-introduction lead': !compact}"
          >
            {{ introduction }}
          </p>
          <nav v-if="crumbs.length > 0" aria-label="breadcrumb">
            <ol class="breadcrumb page-header-breadcrumb mb-0">
              <li v-for="crumb in crumbs" :key="crumb.id" class="breadcrumb-item">
                <router-link v-if="crumb.to" :to="crumb.to">{{ crumb.title }}</router-link>
                <span v-else>{{ crumb.title }}</span>
              </li>
              <li class="breadcrumb-item active" aria-current="page">{{ title }}</li>
            </ol>
          </nav>
        </slot>
        <div class="page-header-actions ms-auto">
          <slot />
        </div>
      </div>
    </template>
  </header>
</template>
<script lang="ts" setup>
import {TreeNodeCore, TreeNodePage} from '@/stores/apolloPlatform/cores/portalNavigation';

defineProps<{title: string; introduction?: string; compact?: boolean}>();

const route = useRoute();
const router = useRouter();

// Ancestors of the current page, outermost first. Without a navigation node (preview) there are none.
const crumbs = computed(() => {
  const result: {id: string; title: string; to?: {name: string}}[] = [];
  let node: TreeNodeCore | TreeNodePage | undefined = (route.meta.node as TreeNodePage | undefined)
    ?.parent;
  while (node) {
    const name = node.node_type === 'CoreNode' ? `core-${node.id}` : `page-${node.id}`;
    result.unshift({id: name, title: node.title, to: router.hasRoute(name) ? {name} : undefined});
    node = node.node_type === 'PageNode' ? node.parent : undefined;
  }
  return result;
});
</script>
<style lang="scss" scoped>
$container-padding-x: 2rem; // See _variables.scss

// The header is wider than any container: it spans the whole screen width, also under a docked
// sidebar (which floats over it, as when it peeks). The page content starts after the sidebar.
// --shell-sidebar-offset is set by ApplicationShell.
.page-header {
  --page-header-width: calc(100vw - var(--shell-sidebar-offset, 0px));
  --page-header-container: var(--page-max-width, 100vw);
  --page-header-gutter: #{$container-padding-x};
  --page-header-spare: calc(
    var(--page-header-width) - min(var(--page-header-width), var(--page-header-container))
  );

  // Reaches the screen's left edge, whatever the parent's width and offset; text starts where the
  // page content does (the offset plus half of what the container leaves spare).
  width: 100vw;
  margin-block-start: calc(-1 * var(--shell-main-padding-top, 0px)); // Hug the top bar
  margin-inline-start: calc(50% - (100vw + var(--shell-sidebar-offset, 0px)) / 2);
  padding-inline: calc(var(--page-header-spare) / 2 + var(--page-header-gutter));
  padding-inline-start: calc(
    var(--shell-sidebar-offset, 0px) + var(--page-header-spare) / 2 + var(--page-header-gutter)
  );
  background: var(--ekco-gradient);
  background-attachment: fixed; // Same as ShellHeader's, so the two gradients join up
  color: #ffffff;

  // Browsers drop the gradient when printing, so the text turns black there.
  @media print {
    color: #000000;
  }
}

// Page actions (print, access, edit) sit on the gradient: white icons like the text, no button background.
// Disabled (loading) stays white at full opacity, so nothing changes while a page loads.
.page-header-actions :deep(.btn-toolbar .btn) {
  --bs-btn-color: #ffffff;
  --bs-btn-bg: transparent;
  --bs-btn-border-color: transparent;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: transparent;
  --bs-btn-hover-border-color: transparent;
  --bs-btn-active-color: #ffffff;
  --bs-btn-active-bg: transparent;
  --bs-btn-active-border-color: transparent;
  --bs-btn-disabled-color: #ffffff;
  --bs-btn-disabled-bg: transparent;
  --bs-btn-disabled-border-color: transparent;
  --bs-btn-disabled-opacity: 1;
  --bs-btn-padding-x: 0.375rem;

  margin-left: 0;
  font-size: 1.25rem;
}

// Same gap between every icon, also across the button groups.
.page-header-actions :deep(.btn-toolbar),
.page-header-actions :deep(.btn-toolbar .btn-group) {
  gap: 0;
}

.page-header-actions :deep(.btn-toolbar .btn-group > .btn-group:not(:first-child)) {
  margin-left: 0;
}

// On wide screens a long introduction wraps at 30% of the screen instead of running across the header.
.page-header-introduction {
  @media (width >= 1200px) {
    max-width: 30vw;
  }
}

.page-header-row {
  min-height: 2.25rem; // Height of the icon buttons
}

// Compact: a bar (dashboards) with a heading-03 sized title, as tall as a page's header with a
// title and a breadcrumb: padding (2 x 1.5rem) + title (3.125rem) + row (1rem gap + 2.25rem).
.page-header-compact {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 6rem; // Title + introduction row, so the notice variant is as tall

  @media (width >= 768px) {
    min-height: 9.375rem;
  }
}

.page-header-notice {
  min-height: 4rem;
}

.page-header-compact .page-header-title {
  margin-bottom: 0;
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.75rem;
}

.page-header-breadcrumb {
  --bs-breadcrumb-divider-color: currentcolor;
  --bs-breadcrumb-item-active-color: currentcolor;

  font-size: 0.875rem;

  a {
    color: inherit;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
